import React, { useEffect, useState } from 'react';
import { Modal } from '../../../components/Modal';
import { Button } from '../../../components/Button';
import { C } from '../../../styles/theme';
import { Sym } from '../../../ui';
import type { FarmInfo, CreateFarmInput, UpdateFarmInput, GeoLocation } from '@grosync/common';
import { LocationPicker } from './LocationPicker';

interface FarmFormModalProps {
  open: boolean;
  farm?: FarmInfo | null;
  onClose: () => void;
  onSubmit: (body: CreateFarmInput | UpdateFarmInput) => void;
  isLoading?: boolean;
}

export const FARM_ICONS = ['agriculture', 'potted_plant', 'eco', 'water_drop', 'grass', 'forest'] as const;

const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  background: C.bg,
  border: `1px solid ${C.border}`,
  borderRadius: 6,
  color: C.text1,
  fontSize: 13,
  boxSizing: 'border-box',
};

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 12,
  color: C.text2,
  marginBottom: 5,
};

export function FarmFormModal({
  open,
  farm,
  onClose,
  onSubmit,
  isLoading,
}: FarmFormModalProps): React.ReactElement {
  const isEdit = !!farm;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [cropType, setCropType] = useState('');
  const [icon, setIcon] = useState<string>('agriculture');
  const [location, setLocation] = useState<GeoLocation | null>(null);

  useEffect(() => {
    if (farm) {
      setName(farm.name);
      setDescription(farm.description ?? '');
      setCropType(farm.cropType ?? '');
      setIcon(farm.icon ?? 'agriculture');
      setLocation(farm.location ?? null);
    } else {
      setName('');
      setDescription('');
      setCropType('');
      setIcon('agriculture');
      setLocation(null);
    }
  }, [farm, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      cropType: cropType.trim() || undefined,
      icon,
      location: location ?? undefined,
    });
  };

  return (
    <Modal open={open} title={isEdit ? '농장 수정' : '농장 추가'} onClose={onClose} width={520}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={LABEL}>이름 *</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={INPUT}
            placeholder="예: A동 토마토 농장"
            required
            maxLength={100}
          />
        </div>

        <div>
          <label style={LABEL}>작물</label>
          <input
            value={cropType}
            onChange={(e) => setCropType(e.target.value)}
            style={INPUT}
            placeholder="예: 토마토 · 딸기"
            maxLength={100}
          />
        </div>

        <div>
          <label style={LABEL}>아이콘</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {FARM_ICONS.map((ic) => (
              <button
                key={ic}
                type="button"
                onClick={() => setIcon(ic)}
                aria-label={ic}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  cursor: 'pointer',
                  border: icon === ic ? `2px solid ${C.primary}` : `1px solid ${C.border}`,
                  background: C.bg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Sym name={ic} size={20} fill={icon === ic ? 1 : 0} style={{ color: C.text1 }} />
              </button>
            ))}
          </div>
        </div>

        <div>
          <label style={LABEL}>위치 · 주소</label>
          <LocationPicker value={location} onChange={setLocation} />
        </div>

        <div>
          <label style={LABEL}>설명</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ ...INPUT, minHeight: 60, resize: 'vertical' }}
            placeholder="농장에 대한 메모"
            maxLength={500}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <Button type="button" variant="ghost" onClick={onClose}>취소</Button>
          <Button type="submit" loading={isLoading} disabled={!name.trim()}>
            {isEdit ? '저장' : '추가'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
